import type {
  ChangeEvent,
  ElementType,
  MouseEvent,
  ReactNode,
  RefObject,
} from 'react';
import { useState } from 'react';

import type { ApiFileUploadType } from '#/api/types/ApiFileUploadType';
import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import type { FieldCustomErrorMatcher } from '#/components/Form/Field/FieldCustomErrorMatcher';
import { InputField } from '#/components/Form/InputField';
import { useI18n } from '#/i18n/hooks/useI18n';
import { type Spacing, spacing } from '#/theme/theme';
import { removeArrayIndex } from '#/utils/functional/array';
import { uuid } from '#/utils/functional/string/uuid';

// TODO: replace with a factory so we stop piling up more unrelated props here
export type MultiInputExtraData = {
  customErrorMatch?: FieldCustomErrorMatcher;
  includeCity?: boolean;
  includeFull?: boolean;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  fileType?: ApiFileUploadType;
  mimetypes?: string[];
  type?: 'FILE' | 'URL' | 'TEXT' | 'PHONE' | 'OPTION_SINGLE' | 'OPTION_MANY';
  options?: Record<'value', string>[];
  downloadAttachment?: boolean;
  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  onUpdateValue?: (...args: any[]) => any;
  listingType?: string;
  qaId?: string;
  parentQuestionIndex?: number;
  optionIndex?: number;
};

type Props<TOption> = {
  id?: string;
  component?: ElementType | null | undefined;
  name: string;
  values: (string | Record<string, unknown>)[];
  errorRef?: RefObject<HTMLDivElement | null>;
  maxInputs: number;
  onChange: (event: (oldValues: unknown[]) => unknown[]) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  extraData?: MultiInputExtraData[];
  hideControls?: boolean;
  addText?: string;
  removeText?: string;
  showEmpty?: boolean;
  qaId: string;
  itemSpacing?: Spacing;
  newItemFactory?: (index: number) => TOption;
  removeButtonAlignment?: MultiInputRemoveButtonAlignment;
};

// NOTE: we could also add { top: number } to 'start' | 'center' here for more
// control, or use the same pattern as in ReactSelect to keep the component
// composable:
// <ReactSelect ...>
// components={{
//     DropdownIndicator,
//     ClearIndicator,
//     MultiValueRemove,
// }}
// ...

export type MultiInputRemoveButtonAlignment =
  | 'center'
  | { top: number | Spacing };

function MultiInputRemoveButtonHolder({
  children,
  alignment,
}: {
  children: ReactNode;
  alignment: MultiInputRemoveButtonAlignment;
}) {
  if (typeof alignment === 'object' && 'top' in alignment) {
    return (
      <Box alignSelf="start" marginTop={alignment.top}>
        {children}
      </Box>
    );
  }

  return <Box>{children}</Box>;
}

export function MultiInput<TOption>({
  id,
  component,
  name,
  values,
  errorRef,
  maxInputs,
  onChange,
  type = 'text',
  placeholder,
  disabled = false,
  extraData = [],
  hideControls = false,
  addText,
  showEmpty,
  qaId,
  itemSpacing = '8px',
  newItemFactory,
  removeButtonAlignment: requestedRemoveButtonAlignment = 'center',
}: Props<TOption>) {
  const { i18n } = useI18n();

  const inputs = values.slice(0, maxInputs).map((value, index) => ({
    value,
    index,
  }));

  const Component = component ?? InputField;
  const [ids, setIds] = useState(() =>
    Array.from({ length: values.length || 1 }).map(() => uuid()),
  );

  const onInputChange =
    (index: number) => (ev: ChangeEvent<HTMLInputElement>) => {
      let update;

      const valuesToAdd = ev?.target ? ev.target.value : ev;

      if (showEmpty && values.length > 1 && !ev) {
        update = (oldValues: unknown[]) => removeArrayIndex(oldValues, index);

        setIds(removeArrayIndex(ids, index));
      } else {
        update = (oldValues: unknown[]) => {
          const newValues = [...oldValues];
          newValues[index] = valuesToAdd;

          if (showEmpty && newValues.length >= values.length) {
            newValues.push('');
          }

          if (newValues.length > values.length) {
            setIds([...ids, uuid()]);
          }

          return newValues;
        };
      }

      onChange(update);
    };

  const addNewInput = () => {
    const newValue = newItemFactory?.(values.length) ?? '';
    const newValues = [...values, newValue];
    onChange(() => newValues);
    setIds([...ids, uuid()]);
  };

  const removeInputAtIndex = (index: number) => {
    onChange(() => removeArrayIndex(values, index));
    setIds(removeArrayIndex(ids, index));
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const inputId = (index: any) => {
    if (!id) return undefined;
    return index === 0 ? id : `${id}-${index + 1}`;
  };

  // Ensure that the remove button is aligned with the main Input when error
  // messages are displayed. Additionally, the default Input also requires a
  // small adjustment, hence the spacing value.  For other types of inputs (e.g.
  // File) it makes more sense to allow for the previous behaviour (centering)
  // More context:
  // https://app.shortcut.com/idealisttechproduct/story/63340/stub-multiinputfield-text-alignment-is-broken-when-displaying-errors
  const removeButtonAlignment =
    Component === InputField && requestedRemoveButtonAlignment === 'center'
      ? { top: spacing[6] }
      : requestedRemoveButtonAlignment;

  return (
    <>
      {inputs.map(({ index }) => (
        <Box
          key={ids[index] || index}
          style={{
            alignItems: 'center',
            display: 'flex',
            marginBottom: itemSpacing,
          }}
        >
          <Component
            id={inputId(index)}
            name={`${name}.${index}`}
            type={type}
            placeholder={placeholder}
            onChange={onInputChange(index)}
            disabled={disabled}
            qaId={qaId}
            noMargin={component ? undefined : true}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...extraData[index]}
          />
          <div ref={errorRef} />
          {!hideControls && values.length > 1 && (
            <MultiInputRemoveButtonHolder alignment={removeButtonAlignment}>
              <Button
                type="button"
                qaId={`${qaId}-remove-${index}`}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();

                  if (!disabled) {
                    removeInputAtIndex(index);
                  }
                }}
                variant="tertiary-grey"
                icon={{ name: 'trash', type: 'normalized' }}
              />
            </MultiInputRemoveButtonHolder>
          )}
        </Box>
      ))}

      {!hideControls && values.length < maxInputs && (
        <Box mt="16px">
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();

              if (!disabled) {
                addNewInput();
              }
            }}
            qaId={`${qaId}-add`}
            variant="secondary"
            icon={{ name: 'plus', type: 'normalized' }}
            disabled={disabled}
          >
            {addText ? i18n('Add %(text)s', { text: addText }) : null}
          </Button>
        </Box>
      )}
    </>
  );
}
